import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { DeviceDataGroup } from "graphql/types"
import { useEffect, useState } from "react"

type AddDeviceDataGroupDialogProps = {
  open: boolean
  onClose: () => void
  formState: DeviceDataGroup
  onSubmit: (arg: DeviceDataGroup) => void
}

type ValidationErrors = {
  name?: string
}

export const AddDeviceDataGroupDialog = (props: AddDeviceDataGroupDialogProps) => {
  const { open, onClose, formState, onSubmit } = props

  const [localFormState, setLocalFormState] = useState<DeviceDataGroup>(formState)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  useEffect(() => {
    setLocalFormState(formState)
    setValidationErrors({}) // Clear errors when form state changes
  }, [formState])

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!localFormState.name?.trim()) {
      errors.name = "Name ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm()
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setLocalFormState((prevState) => ({ ...prevState, [name]: value }))

    // Clear validation error for this field
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name as keyof ValidationErrors]
        return newErrors
      })
    }
  }

  const handleSave = () => {
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    onClose()
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>
          {localFormState.id ? "Eigenschaft bearbeiten" : "Eingenschaft hinzufügen"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {hasValidationErrors && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                Bitte korrigieren Sie die folgenden Fehler:
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(validationErrors).map(([field, error]) => (
                  <Typography key={field} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <TextField
            size="small"
            name="name"
            label="Name"
            type="text"
            fullWidth
            value={localFormState.name}
            onChange={handleTextChange}
            sx={{ mt: 1 }}
            required
            error={!!validationErrors.name}
            helperText={validationErrors.name}
          />

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFormState.isDefault}
                  onChange={(e) =>
                    setLocalFormState((prevState) => ({
                      ...prevState,
                      isDefault: e.target.checked,
                    }))
                  }
                />
              }
              label="Standard EUT Eigenschaft"
            />
          </FormGroup>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFormState.isAux}
                  onChange={(e) =>
                    setLocalFormState((prevState) => ({
                      ...prevState,
                      isAux: e.target.checked,
                    }))
                  }
                />
              }
              label="Hilfsgerät Eigenschaft"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <StyledButton buttonType="cancel" onClick={handleClose} />
          <StyledButton buttonType="save" onClick={handleSave} disabled={hasValidationErrors} />
        </DialogActions>
      </Dialog>
    </>
  )
}
